import React from "react";
import { ButtonGroup, Button } from "@mui/material";
import LaptopMacIcon from "@mui/icons-material/LaptopMac";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import styles from "./Newsletters.module.css";
import logoDark from "../../img/images/logo.png";
import logoWhite from "../../img/images/logo_white.png";

function LocalEmailPreview({ draft }) {
  const heroFeatureName =
    draft?.template_data?.hero_feature_name ||
    draft?.topic_title ||
    draft?.hero_title ||
    "";
  const heroFeatureDescription =
    draft?.template_data?.hero_feature_description || draft?.hero_copy || "";
  const featureName =
    draft?.template_data?.feature_name || draft?.template_data?.product_name || "";
  const featureDescription =
    draft?.template_data?.feature_description ||
    draft?.template_data?.product_description ||
    "";
  const heroImageUrl = draft?.template_data?.hero_image_url || "";
  // Optional dedicated visual inside the spotlight card. Separate from the
  // standalone hero image above.
  const spotlightImageUrl =
    draft?.template_data?.feature_spotlight_image_url || "";
  // Section toggles — default ON (undefined or true). Editors flip these in
  // the editor to hide a whole block from the final email.
  const featureSpotlightEnabled =
    draft?.template_data?.feature_spotlight_enabled !== false;
  const highlightCapabilityEnabled =
    draft?.template_data?.highlight_capability_enabled !== false;
  const monthLabel = draft?.month
    ? new Date(`${draft.month}-01`).toLocaleString("en-IN", {
        month: "long",
        year: "numeric",
      })
    : "Monthly Newsletter";

  return (
    <div className={styles.emailCanvas}>
      <div className={styles.emailPreheader}>{draft.preheader || " "}</div>
      <div className={styles.emailHeader}>
        <div className={styles.emailHeaderBrand}>
          <img src={logoWhite} alt="Easexpense" className={styles.emailLogo} />
        </div>
        <div className={styles.emailHeaderMeta}>
          <span>{monthLabel}</span>
          <strong>{draft?.topic_title || "Campaign Update"}</strong>
        </div>
      </div>

      <div className={styles.emailIntro}>
        <div className={styles.emailEyebrow}>
          {draft?.template_data?.workspace_name || "Easexpense Newsletter"}
        </div>
        <h2>{draft.hero_title || "Your next newsletter headline"}</h2>
        <p>{draft.hero_copy || "Add content to preview your campaign here."}</p>
      </div>

      {heroImageUrl ? (
        <div className={styles.emailHeroStandalone}>
          <img
            src={heroImageUrl}
            alt={heroFeatureName || "Hero visual"}
            className={styles.emailHeroStandaloneImg}
          />
        </div>
      ) : null}

      {featureSpotlightEnabled && (
        <div
          className={`${styles.emailHeroCard} ${
            !spotlightImageUrl ? styles.emailHeroCardNoVisual : ""
          }`}
        >
          <div className={styles.emailHeroContent}>
            <div className={styles.emailHeroBadge}>
              {draft?.topic_type === "new_product" ? "Product Update" : "Feature Spotlight"}
            </div>
            <h3>{heroFeatureName || "Featured highlight"}</h3>
            <p>{heroFeatureDescription || "Use this section to frame the core update, highlight the value, and set the tone for the rest of the newsletter."}</p>
            <div className={styles.emailHeroActions}>
              <a
                href={draft.cta_url || "#preview"}
                className={styles.emailCta}
                onClick={(e) => e.preventDefault()}
              >
                {draft.cta_text || "Open Dashboard"}
              </a>
            </div>
          </div>
          {spotlightImageUrl ? (
            <div className={styles.emailHeroVisual}>
              <img
                src={spotlightImageUrl}
                alt={heroFeatureName || "Spotlight visual"}
                className={styles.emailHeroImage}
              />
            </div>
          ) : null}
        </div>
      )}

      {highlightCapabilityEnabled && featureName && (
        <div className={styles.featureCard}>
          <div className={styles.featurePill}>Highlighted Capability</div>
          <h3>{featureName}</h3>
          <p>{featureDescription}</p>
        </div>
      )}

      <div className={styles.emailSections}>
        {(draft.sections || []).map((section, index) => (
          <div
            className={`${styles.emailSection} ${
              section.theme === "dark"
                ? styles.emailSectionDark
                : styles.emailSectionLight
            }`}
            key={index}
          >
            <div className={styles.emailSectionHeader}>
              <div>
                <h3>{section.title || `Section ${index + 1}`}</h3>
              </div>
            </div>
            {section.image_url ? (
              <div className={styles.emailSectionMedia}>
                <img src={section.image_url} alt={section.title || "Section visual"} />
              </div>
            ) : null}
            <p>{section.body}</p>
            {Array.isArray(section.bullets) && section.bullets.length > 0 && (
              <ul className={styles.emailBullets}>
                {section.bullets.map((bullet, bulletIndex) => (
                  <li key={bulletIndex}>{bullet}</li>
                ))}
              </ul>
            )}
            {section.cta_text && section.cta_url ? (
              <a
                href={section.cta_url}
                className={styles.emailSectionCta}
                onClick={(e) => e.preventDefault()}
              >
                {section.cta_text}
              </a>
            ) : null}
          </div>
        ))}
      </div>

      <div className={styles.emailFooter}>
        <div className={styles.emailFooterBrand}>
          <img src={logoDark} alt="Easexpense" className={styles.emailFooterLogo} />
        </div>
        <div className={styles.emailFooterMeta}>
          <p>1007 Orange St, 4th Floor, Wilmington, DE 19801</p>
          <p>
            Company Number: 9773915618
            <br />
            &copy; Easexpense LLC
          </p>
        </div>
      </div>
    </div>
  );
}

function NewsletterPreview({
  htmlPreview,
  draft,
  viewport,
  setViewport,
  previewError,
}) {
  return (
    <div className={styles.previewPanel}>
      <div className={styles.previewHeader}>
        <div>
          <h3>Email Preview</h3>
          <p>Preview the responsive branded newsletter before approval and send.</p>
        </div>
        <div className={styles.previewControls}>
          <ButtonGroup size="small" className={styles.previewToggle}>
            <Button
              className={
                viewport === "desktop"
                  ? styles.previewToggleActive
                  : styles.previewToggleIdle
              }
              onClick={() => setViewport("desktop")}
              startIcon={<LaptopMacIcon />}
            >
              Desktop
            </Button>
            <Button
              className={
                viewport === "mobile"
                  ? styles.previewToggleActive
                  : styles.previewToggleIdle
              }
              onClick={() => setViewport("mobile")}
              startIcon={<SmartphoneIcon />}
            >
              Mobile
            </Button>
          </ButtonGroup>
        </div>
      </div>

      <div
        className={`${styles.previewFrameWrap} ${
          viewport === "mobile" ? styles.previewMobile : styles.previewDesktop
        }`}
      >
        {htmlPreview ? (
          <iframe
            title="Newsletter Preview"
            srcDoc={htmlPreview}
            sandbox="allow-same-origin"
            style={{
              width: "100%",
              height: viewport === "mobile" ? "780px" : "1100px",
              border: "0",
              borderRadius: "12px",
              background: "#eef3f8",
              display: "block",
            }}
          />
        ) : (
          <LocalEmailPreview draft={draft} />
        )}
      </div>

      {previewError ? <p className={styles.previewHint}>{previewError}</p> : null}
    </div>
  );
}

export default NewsletterPreview;
